<script setup lang="ts">
import lodashSortBy from 'lodash-es/sortBy';
import lodashToNumber from 'lodash-es/toNumber';
import lodashToString from 'lodash-es/toString';
import type { PropType } from 'vue';

import { FiveLevelStateOfUse, type SaleAssetBusEntity, ThreeLevelStateOfUse } from '~/apiClient';
import type { AssetSpecItem, AssetSpecsGroup, AssetSpecsSection, AssetSpecColor } from '~/types/asset.type';
import type { SaleItemEntity } from '~/types/sale.type';

// PROPS
const { sale, assetId } = defineProps({
    sale: { type: Object as PropType<SaleItemEntity>, required: true },
    assetId: { type: String, default: undefined },
});

// COMPOSABLES
const { tt, formatUnits, td } = useTypedI18n();
const { locale } = useTypedI18n();
const { isBundleMono } = useSaleType(() => sale.type);
const { getLocalizedCountry } = useCountries();
const { getPlatformBusClassesLabel } = usePlatformBusClass();

// DATA
const sortedAssets = computed(() => sortSaleAssets(sale));
const asset = computed(() => (assetId ? sale.assets.find(_a => _a.id === assetId)! : sale.mainAsset));

const mergedNotes = computed(() => {
    const documentCountry = getLocalizedCountry(asset.value.registrationDocumentCountry);
    const additionalNotes = asset.value.additionalNotes[locale.value];
    const splitAdditionalNotes = additionalNotes ? additionalNotes.split('\n') : [];

    return [
        ...splitAdditionalNotes,
        ...sortVehicleRemarks(asset.value.vehicleRemarks).map(remark => tt(`asset.vehicleRemarks.${remark}`, { documentCountry })),
    ];
});

const formattedLocation = computed(() => {
    const { address } = asset.value;

    if (!address) return null;

    const country = getLocalizedCountry(address.countryCode) ?? address.country;

    return `${address.alternate ?? address.region}, ${country}`;
});

const bundleSpecs = computed<AssetSpecsSection>(() =>
    sortedAssets.value.map(_a => [
        ['asset.firstRegistration', td(_a.firstRegistration, 'mmyyyyUTC')],
        ['asset.kilometers', formatUnits.km(_a.km)],
        ['asset.vehicleCondition.base', tt(`asset.vehicleCondition.${_a.vehicleCondition}`)],
        ...insertIf<AssetSpecItem>(!!_a.useType, () => ['asset.useType.base', tt(`asset.useType.${_a.useType!}`)]),
        ['asset.vin', _a.vin],
        ['asset.inspectionExpiryDate', td(_a.inspectionExpiryDate, 'mmyyyyUTC')],
        ['asset.registrationDocumentCountry', getLocalizedCountry(_a.registrationDocumentCountry)],
    ])
);

const generalInformationSpecs = computed<AssetSpecsSection>(() => [
    ...insertIf<AssetSpecsGroup>(
        isBundleMono.value,
        [
            ...insertIf<AssetSpecItem>(!!asset.value.euroNorm, () => ['asset.antiPollution', tt(`asset.euroNorm.${asset.value.euroNorm!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.class, () => ['asset.class.base', getPlatformBusClassesLabel(asset.value.class!)]),
            ['asset.version', asset.value.version.versionValue],
            ['asset.location', formattedLocation.value],
            [
                'asset.visitDate.base',
                asset.value.visitDateFrom && asset.value.visitDateTo
                    ? tt('asset.visitDate.fromTo', {
                          date: td(asset.value.visitDateFrom, 'ddmmyyyyUTC'),
                          fromTime: td(removeTimezone(asset.value.visitDateFrom), 'hh'),
                          toTime: td(removeTimezone(asset.value.visitDateTo), 'hh'),
                      })
                    : null,
            ],
        ],
        [
            ['asset.converter', asset.value.bodyBuilder],
            ['asset.deckType.base', tt(`asset.deckType.${asset.value.deckType}`)],
            ['asset.length', formatUnits.meters(asset.value.length)],
            ['asset.width', formatUnits.meters(asset.value.width)],
            ['asset.height', formatUnits.meters(asset.value.height)],
        ]
    ),
    ...insertIf<AssetSpecsGroup>(
        !isBundleMono.value,
        [
            ['asset.firstRegistration', td(asset.value.firstRegistration, 'mmyyyyUTC')],
            ['asset.kilometers', formatUnits.km(asset.value.km)],
            ...insertIf<AssetSpecItem>(!!asset.value.euroNorm, () => ['asset.antiPollution', tt(`asset.euroNorm.${asset.value.euroNorm!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.type, () => ['asset.busType.base', tt(`asset.busType.${asset.value.type!}`)]),
            ...insertIf<AssetSpecItem>(!!asset.value.class, () => ['asset.class.base', getPlatformBusClassesLabel(asset.value.class!)]),
            ['asset.version', asset.value.version.versionValue],
            ['asset.location', formattedLocation.value],
            [
                'asset.visitDate.base',
                asset.value.visitDateFrom && asset.value.visitDateTo
                    ? tt('asset.visitDate.fromTo', {
                          date: td(asset.value.visitDateFrom, 'ddmmyyyyUTC'),
                          fromTime: td(removeTimezone(asset.value.visitDateFrom), 'hh'),
                          toTime: td(removeTimezone(asset.value.visitDateTo), 'hh'),
                      })
                    : null,
            ],
        ],
        [
            ['asset.vehicleCondition.base', tt(`asset.vehicleCondition.${asset.value.vehicleCondition}`)],
            ...insertIf<AssetSpecItem>(!!asset.value.useType, () => ['asset.useType.base', tt(`asset.useType.${asset.value.useType!}`)]),
            ['asset.vin', asset.value.vin],
            ['asset.converter', asset.value.bodyBuilder],
            ['asset.deckType.base', tt(`asset.deckType.${asset.value.deckType}`)],
            ['asset.length', formatUnits.meters(asset.value.length)],
            ['asset.width', formatUnits.meters(asset.value.width)],
            ['asset.height', formatUnits.meters(asset.value.height)],
            ...insertIf<AssetSpecItem>(asset.value.doubleDecker, ['asset.doubleDecker', asset.value.doubleDecker]),
            ['asset.inspectionExpiryDate', td(asset.value.inspectionExpiryDate, 'mmyyyyUTC')],
            ['asset.registrationDocumentCountry', getLocalizedCountry(asset.value.registrationDocumentCountry)],
        ]
    ),
    [
        ['asset.doors.base', tt(`asset.doors.${asset.value.doors}`)],
        ['asset.totalCapacity', asset.value.totalCapacity],
        ['asset.seats', asset.value.seats],
        ['asset.seatsFromS1', asset.value.seatsFromS1],
        ['asset.standingPlaces', asset.value.standingPlaces],
        ['asset.standingPlacesFromS2', asset.value.standingPlacesFromS2],
        ['asset.companionSeats', asset.value.companionSeats],
        ['asset.foldingSeats', asset.value.foldingSeats],
        ['asset.wheelchairPlaces', asset.value.wheelchairPlaces],
        ...insertIf<AssetSpecItem>(!!asset.value.wheelchairRamp, () => [
            'asset.wheelchairRamp.base',
            tt(`asset.wheelchairRamp.${asset.value.wheelchairRamp!}`),
        ]),
        ['asset.wheelchairRampWorking', asset.value.wheelchairRampWorking],
    ],
    sortFalseLast([
        ['asset.luggageCapacity', formatUnits.cubicMeters(lodashToNumber(asset.value.luggageCapacity))],
        ['asset.passThroughLuggageCompartment', asset.value.passThroughLuggageCompartment],
    ]),
]);

const conditionsSpecs = computed<AssetSpecsSection>(() =>
    isBundleMono.value ? (sortedAssets.value.map(_a => getConditionSpecs(_a)) ?? []) : [getConditionSpecs(asset.value)]
);

const gearboxSpecs = computed<AssetSpecsSection>(() => [
    [
        ...insertIf<AssetSpecItem>(!!asset.value.gearboxType, () => ['asset.gearboxType.base', tt(`asset.gearboxType.${asset.value.gearboxType!}`)]),
        ['asset.gearboxBrand', asset.value.gearboxBrand],
        ['asset.gearboxModel', asset.value.gearboxModel],
    ],
    ...insertIf<AssetSpecsGroup>(asset.value.certifiedGearboxMaintenance, [
        ['asset.certifiedGearboxMaintenance', asset.value.certifiedGearboxMaintenance],
        ['asset.gearboxMaintenanceMileage', formatUnits.km(asset.value.gearboxMaintenanceMileage)],
    ]),
]);

const engineSpecs = computed<AssetSpecsSection>(() => [
    [
        ['asset.fuel.base', tt(`asset.fuel.${asset.value.fuel}`)],
        ['asset.engineBrand', asset.value.engineBrand],
        ['asset.engineModel', asset.value.engineModel],
        ['asset.enginePosition', tt(`asset.position.${asset.value.enginePosition}`)],
    ],
    [
        ['asset.displacement', formatUnits.cc(asset.value.displacement)],
        ['asset.hp', asset.value.hp],
        ['asset.kw', formatUnits.kw(asset.value.kw)],
        ['asset.cylinders', asset.value.cylinders],
    ],
    [
        ['asset.fuelTankCapacity', formatUnits.liters(asset.value.fuelTankCapacity)],
        ['asset.cngTanks', asset.value.cngTanks],
        ...insertIf<AssetSpecItem>(!!asset.value.cngTanksMaterial, () => [
            'asset.cngTanksMaterial.base',
            tt(`asset.cngTanksMaterial.${asset.value.cngTanksMaterial!}`),
        ]),
        ['asset.batteryPower', formatUnits.kwh(asset.value.batteryPower)],
        ...insertIf<AssetSpecItem>(!!asset.value.chargingType, () => [
            'asset.chargingType.base',
            tt(`asset.chargingType.${asset.value.chargingType!}`),
        ]),
    ],
    ...insertIf<AssetSpecsGroup>(asset.value.certifiedEngineMaintenance, [
        ['asset.certifiedEngineMaintenance', asset.value.certifiedEngineMaintenance],
        ['asset.engineMaintenanceMileage', formatUnits.km(asset.value.engineMaintenanceMileage)],
    ]),
]);

const wheelsSpecs = computed<AssetSpecsSection>(() => [
    sortFalseLast([
        ['asset.axles', asset.value.axles],
        ['asset.wheels', asset.value.wheels],
        ['asset.wheelsSizeAxle1', asset.value.wheelsSizeAxle1],
        ['asset.wheelsSizeAxle2', asset.value.wheelsSizeAxle2],
        ['asset.wheelsSizeAxle3', asset.value.wheelsSizeAxle3],
        ['asset.spareWheel', asset.value.spareWheel],
        ['asset.alloyWheels', asset.value.alloyWheels],
    ]),
]);

const accessoriesSpecs = computed<AssetSpecsSection>(() => [
    sortFalseLast([
        ['asset.acDriver', asset.value.acDriver],
        ['asset.acPassengers', asset.value.acPassengers],
        ['asset.heater', asset.value.heater],
        ['asset.preHeater', asset.value.preHeater],
    ]),
    sortFalseLast([
        ['asset.cruiseControl.base', tt(`asset.cruiseControl.${asset.value.cruiseControl}`)],
        ['asset.tachograph.base', tt(`asset.tachograph.${asset.value.tachograph}`)],
        ['asset.abs', asset.value.abs],
        ['asset.asr', asset.value.asr],
        ['asset.retarder', asset.value.retarder],
        ['asset.breathalyzer', asset.value.breathalyzer],
    ]),
    sortFalseLast([
        ['asset.cctv', asset.value.cctv],
        ['asset.internalCameras', asset.value.internalCameras],
        ['asset.externalCameras', asset.value.externalCameras],
        ['asset.retrocamera', asset.value.retrocamera],
    ]),
    [
        ['asset.firePreventionSystem', asset.value.firePreventionSystem],
        ['asset.firePreventionSystemBrand', asset.value.firePreventionSystemBrand],
    ],
    [
        ['asset.indicatorSigns', asset.value.indicatorSigns],
        ['asset.indicatorSignsBrand', asset.value.indicatorSignsBrand],
    ],
    sortFalseLast([
        ['asset.driverBerth', asset.value.driverBerth],
        ['asset.socket220', asset.value.socket220],
    ]),
    sortFalseLast([
        ['asset.wifi', asset.value.wifi],
        ['asset.microphone', asset.value.microphone],
        ['asset.cdPlayer', asset.value.cdPlayer],
        ['asset.dvdPlayer', asset.value.dvdPlayer],
        ['asset.usbPlayer', asset.value.usbPlayer],
        ['asset.monitors', asset.value.monitors],
        ['asset.mp3', asset.value.mp3],
    ]),
    sortFalseLast([
        ['asset.kitchen', asset.value.kitchen],
        ['asset.coffeeMachine', asset.value.coffeeMachine],
        ['asset.refrigerator', asset.value.refrigerator],
        ['asset.toilet', asset.value.toilet],
    ]),
    sortFalseLast([
        ...insertIf<AssetSpecItem>(!!asset.value.glasses, () => ['asset.glasses.base', tt(`asset.glasses.${asset.value.glasses!}`)]),
        ['asset.openableWindows', asset.value.openableWindows],
        ['asset.curtains', asset.value.curtains],
    ]),
    sortFalseLast([
        ['asset.seatsMonitor', asset.value.seatsMonitor],
        ['asset.seatsRadio', asset.value.seatsRadio],
        ['asset.seatsUsbPorts', asset.value.seatsUsbPorts],
        ['asset.seatsSocket220', asset.value.seatsSocket220],
    ]),
    sortFalseLast([
        ...insertIf<AssetSpecItem>(!!asset.value.seatUpholstery, () => [
            'asset.seatUpholstery.base',
            tt(`asset.seatUpholstery.${asset.value.seatUpholstery!}`),
        ]),
        ['asset.recliningSeats', asset.value.recliningSeats],
        ['asset.extendableSeats', asset.value.extendableSeats],
    ]),
    sortFalseLast([
        ['asset.safetyBelts', asset.value.safetyBelts],
        ['asset.coffeeTables', asset.value.coffeeTables],
        ['asset.seatPocket', asset.value.seatPocket],
        ['asset.handRest', asset.value.handRest],
        ['asset.footRest', asset.value.footRest],
        ['asset.hatBox', asset.value.hatBox],
    ]),
]);

// METHODS
const getStateOfUseVariant = (stateOfUse?: ThreeLevelStateOfUse | FiveLevelStateOfUse | null): AssetSpecColor | undefined => {
    switch (stateOfUse) {
        case ThreeLevelStateOfUse.Excellent:
        case FiveLevelStateOfUse.Excellent:
        case FiveLevelStateOfUse.Good:
            return 'success';
        case ThreeLevelStateOfUse.Fair:
        case FiveLevelStateOfUse.Fair:
            return 'warning';
        case ThreeLevelStateOfUse.Critical:
        case FiveLevelStateOfUse.Poor:
        case FiveLevelStateOfUse.Critical:
            return 'error';

        default:
            return undefined;
    }
};

const getConditionSpecs = (_a: SaleAssetBusEntity): AssetSpecsGroup =>
    <AssetSpecItem[]>[
        ...(_a.enginePresent === false
            ? [['asset.engine', tt('asset.stateOfUse.missing')]]
            : [
                  ...insertIf(!!_a.stateOfUseEngine, () => [
                      'asset.stateOfUse.engine.base',
                      tt(`asset.stateOfUse.engine.${_a.stateOfUseEngine!}`),
                      getStateOfUseVariant(_a.stateOfUseEngine),
                  ]),
              ]),
        ...(_a.gearboxPresent === false
            ? [['asset.gearbox', tt('asset.stateOfUse.missing')]]
            : [
                  ...insertIf(!!_a.stateOfUseGearbox, () => [
                      'asset.stateOfUse.gearbox.base',
                      tt(`asset.stateOfUse.gearbox.${_a.stateOfUseGearbox!}`),
                      getStateOfUseVariant(_a.stateOfUseGearbox),
                  ]),
              ]),
        ...insertIf(!!_a.stateOfUseAirConditioner, () => [
            'asset.stateOfUse.airConditioner.base',
            tt(`asset.stateOfUse.airConditioner.${_a.stateOfUseAirConditioner!}`),
            getStateOfUseVariant(_a.stateOfUseAirConditioner),
        ]),
        ...insertIf(!!_a.stateOfUseCompressedAirSystem, () => [
            'asset.stateOfUse.compressedAirSystem.base',
            tt(`asset.stateOfUse.compressedAirSystem.${_a.stateOfUseCompressedAirSystem!}`),
            getStateOfUseVariant(_a.stateOfUseCompressedAirSystem),
        ]),
        ...insertIf(!!_a.stateOfUseInteriors, () => [
            'asset.stateOfUse.interiors.base',
            tt(`asset.stateOfUse.interiors.${_a.stateOfUseInteriors!}`),
            getStateOfUseVariant(_a.stateOfUseInteriors),
        ]),
        ...insertIf(!!_a.stateOfUseStructureAndBody, () => [
            'asset.stateOfUse.structureBody.base',
            tt(`asset.stateOfUse.structureBody.${_a.stateOfUseStructureAndBody!}`),
            getStateOfUseVariant(_a.stateOfUseStructureAndBody),
        ]),
        ...insertIf(!!_a.stateOfUseTyres, () => [
            'asset.stateOfUse.tyres.base',
            tt(`asset.stateOfUse.tyres.${_a.stateOfUseTyres!}`),
            getStateOfUseVariant(_a.stateOfUseTyres),
        ]),
        ['asset.missingParts', lodashToString(_a.missingParts[locale.value]) || null],
    ];

const removeTimezone = (date: string): string => date.replace(/Z$/, '');

const sortFalseLast = (items: AssetSpecItem[]): AssetSpecItem[] =>
    lodashSortBy(items, [(a): boolean | number => (typeof a[1] === 'boolean' ? !a[1] : 0)]);
</script>

<template>
    <section v-if="asset" class="flex flex-col gap-6">
        <div class="flex flex-col gap-6">
            <h3 class="text-highlighted text-2xl font-semibold md:text-3xl">
                <template v-if="isBundleMono">{{ tt('asset.bundleSpecs') }}</template>
                <template v-else>{{ tt('asset.vehicleSpecs') }}</template>
            </h3>

            <ul v-if="mergedNotes.length" class="list-inside list-disc p-0">
                <li v-for="(note, index) in mergedNotes" :key="index">{{ note }}</li>
            </ul>
        </div>

        <div>
            <SdpSpecsSection v-if="isBundleMono" :title="tt('asset.vehiclesInBundle')" :specs="bundleSpecs" default-open grouped-by-asset grid />

            <SdpSpecsSection :title="tt('asset.generalInformation')" :specs="generalInformationSpecs" :default-open="!isBundleMono" grid />

            <SdpSpecsSection :title="tt('asset.conditionsAndMaintenance')" :specs="conditionsSpecs" :grouped-by-asset="isBundleMono" />

            <SdpSpecsSection :title="tt('asset.engine')" :specs="engineSpecs" grid />

            <SdpSpecsSection :title="tt('asset.gearbox')" :specs="gearboxSpecs" grid />

            <SdpSpecsSection :title="tt('asset.axlesTires')" :specs="wheelsSpecs" grid />

            <SdpSpecsSection :title="tt('asset.accessories')" :specs="accessoriesSpecs" grid />
        </div>
    </section>
</template>
